/**
 * Procedural primitive geometry generators.
 *
 * Each generator returns { vertices, indices, loopNormals, topology, uvs } in
 * object-local space with the Z-up convention matching PoseRef / Blender.
 *
 * Topology (polygon faces) is the authoritative face structure; triangle
 * indices are derived from it via fan triangulation.
 *
 * All geometry uses shared-vertex (BMesh-style) meshes: vertices are unique
 * positions, topology corners reference shared vertex indices, and per-corner
 * data (loopNormals, uvs) is stored per-corner (not per-vertex).
 *
 * All geometry is centered at the origin with 1-unit overall dimensions
 * (0.5 half-extent / radius), sized to roughly half mannequin height.
 */

import { generateCurveGeometry } from "./curveGeometry.ts";
import { createDefaultCurveData } from "./curvemath.ts";
import { recomputeLoopNormals } from "./meshOps.ts";
import {
  buildEdgesAndCornerEdges,
  topologyFromTriangles,
  triangulateTopologyForRendering,
} from "./topology.ts";
import type {
  LoopNormals,
  PolygonTopology,
  PrimitiveKind,
  RenderTriangleIndices,
} from "./types.ts";
import {
  asLoopNormals,
  asSharedTriangleIndices,
  EDGE_WIRE,
  EMPTY_RENDER_TRIANGLE_INDICES,
} from "./types.ts";

export interface PrimitiveGeometry {
  vertices: Float32Array;
  indices: RenderTriangleIndices;
  loopNormals: LoopNormals;
  topology: PolygonTopology;
  uvs: Float32Array;
}

// ---------------------------------------------------------------------------
// Cube
// ---------------------------------------------------------------------------

export interface CubeParams {
  /** Edge length in meters (default 1). */
  size?: number;
  /** Subdivision levels — each level quadruples the face count (default 0). */
  subdivisions?: number;
}

export function generateCube(params: CubeParams = {}): PrimitiveGeometry {
  const size = params.size ?? 1;
  const subdivisions = Math.max(
    0,
    Math.min(4, Math.round(params.subdivisions ?? 0)),
  );
  const s = size * 0.5; // half-extent
  const n = 1 << subdivisions; // grid cells per face edge: 1, 2, 4, 8, 16

  // Six face definitions: [origin, u-axis, v-axis, face-normal]
  // Each face's n×n quad grid is generated with CCW winding — u × v = face normal.
  // prettier-ignore
  const faceDefs: readonly [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
  ][] = [
    [-s, -s, +s, 2 * s, 0, 0, 0, 2 * s, 0, 0, 0, 1], // +Z (top)
    [-s, +s, -s, 2 * s, 0, 0, 0, -2 * s, 0, 0, 0, -1], // -Z (bottom)
    [+s, -s, -s, 0, 2 * s, 0, 0, 0, 2 * s, 1, 0, 0], // +X (right)
    [-s, +s, -s, 0, -2 * s, 0, 0, 0, 2 * s, -1, 0, 0], // -X (left)
    [+s, +s, -s, -2 * s, 0, 0, 0, 0, 2 * s, 0, 1, 0], // +Y (front)
    [-s, -s, -s, 2 * s, 0, 0, 0, 0, 2 * s, 0, -1, 0], // -Y (back)
  ];

  const positions: number[] = [];
  const corners: number[] = [];
  const faceOffsets: number[] = [0];
  const loopNorms: number[] = [];
  const uvData: number[] = [];

  const vertexMap = new Map<string, number>();
  const getVertex = (x: number, y: number, z: number): number => {
    // High-precision welding key, matching the pattern in ballGeom.ts MeshBuilder.
    const key = `${Math.round(x * 1e6)}_${Math.round(y * 1e6)}_${Math.round(z * 1e6)}`;
    let idx = vertexMap.get(key);
    if (idx === undefined) {
      idx = positions.length / 3;
      positions.push(x, y, z);
      vertexMap.set(key, idx);
    }
    return idx;
  };

  for (const [ox, oy, oz, ux, uy, uz, vx, vy, vz, nx, ny, nz] of faceDefs) {
    // (n+1) × (n+1) grid of vertices for this face, using the shared vertex pool
    const grid: number[][] = [];
    for (let j = 0; j <= n; j++) {
      const row: number[] = [];
      for (let i = 0; i <= n; i++) {
        const t = i / n,
          r = j / n;
        row.push(
          getVertex(
            ox + t * ux + r * vx,
            oy + t * uy + r * vy,
            oz + t * uz + r * vz,
          ),
        );
      }
      grid.push(row);
    }

    // n × n quads with flat per-face normals (loop normals)
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) {
        const v00 = grid[j][i];
        const v10 = grid[j][i + 1];
        const v11 = grid[j + 1][i + 1];
        const v01 = grid[j + 1][i];

        corners.push(v00, v10, v11, v01);
        faceOffsets.push(corners.length);
        loopNorms.push(nx, ny, nz, nx, ny, nz, nx, ny, nz, nx, ny, nz);
        uvData.push(
          (i / n) * size,
          (j / n) * size,
          ((i + 1) / n) * size,
          (j / n) * size,
          ((i + 1) / n) * size,
          ((j + 1) / n) * size,
          (i / n) * size,
          ((j + 1) / n) * size,
        );
      }
    }
  }

  const offsets = new Uint32Array(faceOffsets);
  const cornersArr = new Uint32Array(corners);
  const topology: PolygonTopology = {
    offsets,
    corners: cornersArr,
    ...buildEdgesAndCornerEdges(offsets, cornersArr),
  };

  return {
    vertices: new Float32Array(positions),
    indices: triangulateTopologyForRendering(topology),
    loopNormals: asLoopNormals(new Float32Array(loopNorms)),
    topology,
    uvs: new Float32Array(uvData),
  };
}

// ---------------------------------------------------------------------------
// Plane
// ---------------------------------------------------------------------------

export interface PlaneParams {
  /** Edge length in meters (default 1). */
  size?: number;
  /** Subdivision levels — each level quadruples the face count (default 0). */
  subdivisions?: number;
}

export function generatePlane(params: PlaneParams = {}): PrimitiveGeometry {
  const size = params.size ?? 1;
  const subdivisions = Math.max(
    0,
    Math.min(4, Math.round(params.subdivisions ?? 0)),
  );
  const s = size * 0.5; // half-extent
  const n = 1 << subdivisions; // grid cells per edge: 1, 2, 4, 8, 16

  const positions: number[] = [];
  const corners: number[] = [];
  const faceOffsets: number[] = [0];
  const loopNorms: number[] = [];
  const uvData: number[] = [];

  // (n+1) × (n+1) vertex grid on the XY plane, CCW from +Z
  const grid: number[][] = [];
  for (let j = 0; j <= n; j++) {
    const row: number[] = [];
    for (let i = 0; i <= n; i++) {
      row.push(positions.length / 3);
      positions.push(-s + (i / n) * size, -s + (j / n) * size, 0);
    }
    grid.push(row);
  }

  // n × n quads with flat Z-up normals
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      corners.push(
        grid[j][i],
        grid[j][i + 1],
        grid[j + 1][i + 1],
        grid[j + 1][i],
      );
      faceOffsets.push(corners.length);
      loopNorms.push(0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1);
      uvData.push(
        (i / n) * size,
        (j / n) * size,
        ((i + 1) / n) * size,
        (j / n) * size,
        ((i + 1) / n) * size,
        ((j + 1) / n) * size,
        (i / n) * size,
        ((j + 1) / n) * size,
      );
    }
  }

  const offsets = new Uint32Array(faceOffsets);
  const cornersArr = new Uint32Array(corners);
  const topology: PolygonTopology = {
    offsets,
    corners: cornersArr,
    ...buildEdgesAndCornerEdges(offsets, cornersArr),
  };

  return {
    vertices: new Float32Array(positions),
    indices: triangulateTopologyForRendering(topology),
    loopNormals: asLoopNormals(new Float32Array(loopNorms)),
    topology,
    uvs: new Float32Array(uvData),
  };
}

// ---------------------------------------------------------------------------
// Circle
// ---------------------------------------------------------------------------

export interface CircleParams {
  /** Circle radius in meters (default 0.5). */
  radius?: number;
  /** Vertex count around the ring (default 32). */
  segments?: number;
}

export function generateCircle(params: CircleParams = {}): PrimitiveGeometry {
  const radius = params.radius ?? 0.5;
  const segments = params.segments ?? 32;
  const verts = new Float32Array(segments * 3);
  const edges = new Uint32Array(segments * 2);
  const edgeFlags = new Uint8Array(segments).fill(EDGE_WIRE);

  for (let seg = 0; seg < segments; seg++) {
    const theta = (2 * Math.PI * seg) / segments;
    verts[seg * 3] = Math.cos(theta) * radius;
    verts[seg * 3 + 1] = Math.sin(theta) * radius;
    verts[seg * 3 + 2] = 0;

    const next = (seg + 1) % segments;
    edges[seg * 2] = Math.min(seg, next);
    edges[seg * 2 + 1] = Math.max(seg, next);
  }

  const topology: PolygonTopology = {
    offsets: new Uint32Array([0]),
    corners: new Uint32Array(0),
    edges,
    cornerEdges: new Uint32Array(0),
    edgeFlags,
  };

  return {
    vertices: verts,
    indices: EMPTY_RENDER_TRIANGLE_INDICES,
    loopNormals: asLoopNormals(new Float32Array(0)),
    topology,
    uvs: new Float32Array(0),
  };
}

// ---------------------------------------------------------------------------
// Sphere (UV sphere) — shared-vertex with merged seam/poles
// ---------------------------------------------------------------------------

export interface SphereParams {
  /** Sphere radius in meters (default 0.5). */
  radius?: number;
  /** Longitudinal segments (default 32). */
  segments?: number;
  /** Latitudinal rings (default 16). */
  rings?: number;
}

export function generateSphere(params: SphereParams = {}): PrimitiveGeometry {
  const radius = params.radius ?? 0.5;
  const segments = params.segments ?? 32;
  const rings = params.rings ?? 16;

  // Shared vertices: 2 pole vertices + (rings-1) interior rings × segments
  const vertCount = 2 + (rings - 1) * segments;
  const verts = new Float32Array(vertCount * 3);

  // South pole (index 0) — bottom, z = -radius
  verts[0] = 0;
  verts[1] = 0;
  verts[2] = -radius;

  // North pole (index 1) — top, z = +radius
  verts[3] = 0;
  verts[4] = 0;
  verts[5] = radius;

  // Interior ring vertices (ring 1..rings-1), starting at index 2
  for (let ring = 1; ring < rings; ring++) {
    const phi = (Math.PI * ring) / rings;
    const sinPhi = Math.sin(phi);
    const cosPhi = Math.cos(phi);
    for (let seg = 0; seg < segments; seg++) {
      const theta = (2 * Math.PI * seg) / segments;
      const idx = 2 + (ring - 1) * segments + seg;
      verts[idx * 3] = sinPhi * Math.cos(theta) * radius;
      verts[idx * 3 + 1] = sinPhi * Math.sin(theta) * radius;
      verts[idx * 3 + 2] = cosPhi * radius;
    }
  }

  // Topology: triangle fans at poles + quad grid in between
  // Bottom cap: segments triangles, Top cap: segments triangles
  // Middle: (rings-2) rows × segments quads
  const triCapFaces = segments * 2;
  const quadFaces = Math.max(0, rings - 2) * segments;
  const totalFaces = triCapFaces + quadFaces;
  const totalCorners = triCapFaces * 3 + quadFaces * 4;

  const offsets = new Uint32Array(totalFaces + 1);
  const corners = new Uint32Array(totalCorners);

  let ci = 0;
  let fi = 0;

  // Helper to get ring vertex index
  const ringIdx = (ring: number, seg: number) =>
    2 + (ring - 1) * segments + (seg % segments);

  // Bottom cap (south pole, ring=rings → ring rings-1) — triangles
  // South pole is index 0. Ring rings-1 is the bottommost interior ring (z ≈ -radius).
  // Winding: looking from outside (-Z), CCW
  for (let seg = 0; seg < segments; seg++) {
    offsets[fi] = ci;
    fi++;
    corners[ci++] = 0; // south pole
    corners[ci++] = ringIdx(rings - 1, seg + 1);
    corners[ci++] = ringIdx(rings - 1, seg);
  }

  // Middle quad rows (ring r to ring r+1)
  // Winding: CCW from outside — ring advances before seg advances so that
  // the Newell face normal points outward. The caps are also outward-wound,
  // so their dot-product stays above the auto-smooth threshold and the poles
  // smooth correctly. Reversed order (ring,seg → ring,seg+1 → ...) was CW
  // from outside, producing inward face normals that broke smooth grouping.
  for (let ring = 1; ring < rings - 1; ring++) {
    for (let seg = 0; seg < segments; seg++) {
      offsets[fi] = ci;
      fi++;
      corners[ci++] = ringIdx(ring, seg);
      corners[ci++] = ringIdx(ring + 1, seg);
      corners[ci++] = ringIdx(ring + 1, seg + 1);
      corners[ci++] = ringIdx(ring, seg + 1);
    }
  }

  // Top cap (north pole, ring=0 → ring 1) — triangles
  // North pole is index 1. Ring 1 is the topmost interior ring (z ≈ +radius).
  for (let seg = 0; seg < segments; seg++) {
    offsets[fi] = ci;
    fi++;
    corners[ci++] = 1; // north pole
    corners[ci++] = ringIdx(1, seg);
    corners[ci++] = ringIdx(1, seg + 1);
  }

  offsets[totalFaces] = ci;

  const topology: PolygonTopology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };

  // Per-corner UVs (differ across seams even though positions are shared)
  const uvData = new Float32Array(totalCorners * 2);
  let ui = 0;
  let _cornerIdx = 0;

  // UV helper: ring/seg to UV
  const ringV = (ring: number) => 1 - ring / rings;
  const segU = (seg: number) => seg / segments;

  // Bottom cap UVs
  for (let seg = 0; seg < segments; seg++) {
    // pole
    uvData[ui++] = (segU(seg) + segU(seg + 1)) / 2;
    uvData[ui++] = ringV(rings);
    // ring(rings-1), seg+1
    uvData[ui++] = segU(seg + 1);
    uvData[ui++] = ringV(rings - 1);
    // ring(rings-1), seg
    uvData[ui++] = segU(seg);
    uvData[ui++] = ringV(rings - 1);
    _cornerIdx += 3;
  }

  // Middle quad UVs — order matches new winding: (ring,seg), (ring+1,seg), (ring+1,seg+1), (ring,seg+1)
  for (let ring = 1; ring < rings - 1; ring++) {
    for (let seg = 0; seg < segments; seg++) {
      uvData[ui++] = segU(seg);
      uvData[ui++] = ringV(ring);
      uvData[ui++] = segU(seg);
      uvData[ui++] = ringV(ring + 1);
      uvData[ui++] = segU(seg + 1);
      uvData[ui++] = ringV(ring + 1);
      uvData[ui++] = segU(seg + 1);
      uvData[ui++] = ringV(ring);
      _cornerIdx += 4;
    }
  }

  // Top cap UVs
  for (let seg = 0; seg < segments; seg++) {
    // pole
    uvData[ui++] = (segU(seg) + segU(seg + 1)) / 2;
    uvData[ui++] = ringV(0);
    // ring1, seg
    uvData[ui++] = segU(seg);
    uvData[ui++] = ringV(1);
    // ring1, seg+1
    uvData[ui++] = segU(seg + 1);
    uvData[ui++] = ringV(1);
    _cornerIdx += 3;
  }

  const vertices = new Float32Array(verts);

  return {
    vertices,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: recomputeLoopNormals(vertices, topology),
    topology,
    uvs: uvData,
  };
}

// ---------------------------------------------------------------------------
// Torus — shared-vertex with both seams wrapped
// ---------------------------------------------------------------------------

export interface TorusParams {
  /** Tube path radius (default 0.5). */
  majorRadius?: number;
  /** Cross-section radius (default 0.15). */
  minorRadius?: number;
  /** Segments around the tube path (default 48). */
  majorSegments?: number;
  /** Segments around the cross-section (default 24). */
  minorSegments?: number;
}

export function generateTorus(params: TorusParams = {}): PrimitiveGeometry {
  const majorRadius = params.majorRadius ?? 0.5;
  const minorRadius = params.minorRadius ?? 0.15;
  const majorSegments = params.majorSegments ?? 48;
  const minorSegments = params.minorSegments ?? 24;
  // Shared vertices: majorSegments × minorSegments (both seams wrap)
  const vertCount = majorSegments * minorSegments;
  const verts = new Float32Array(vertCount * 3);

  for (let i = 0; i < majorSegments; i++) {
    const u = (2 * Math.PI * i) / majorSegments;
    const cosU = Math.cos(u);
    const sinU = Math.sin(u);

    for (let j = 0; j < minorSegments; j++) {
      const v = (2 * Math.PI * j) / minorSegments;
      const cosV = Math.cos(v);
      const sinV = Math.sin(v);

      const idx = i * minorSegments + j;
      verts[idx * 3] = (majorRadius + minorRadius * cosV) * cosU;
      verts[idx * 3 + 1] = (majorRadius + minorRadius * cosV) * sinU;
      verts[idx * 3 + 2] = minorRadius * sinV;
    }
  }

  // Topology: majorSegments × minorSegments quad faces, both seams wrap
  const totalFaces = majorSegments * minorSegments;
  const totalCorners = totalFaces * 4;
  const offsets = new Uint32Array(totalFaces + 1);
  const corners = new Uint32Array(totalCorners);

  const vidx = (i: number, j: number) =>
    (i % majorSegments) * minorSegments + (j % minorSegments);

  let ci = 0;
  for (let i = 0; i < majorSegments; i++) {
    for (let j = 0; j < minorSegments; j++) {
      const fi = i * minorSegments + j;
      offsets[fi] = ci;
      corners[ci++] = vidx(i, j);
      corners[ci++] = vidx(i + 1, j);
      corners[ci++] = vidx(i + 1, j + 1);
      corners[ci++] = vidx(i, j + 1);
    }
  }
  offsets[totalFaces] = ci;

  const topology: PolygonTopology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };

  // Per-corner UVs (differ across seams)
  const uvData = new Float32Array(totalCorners * 2);
  let ui = 0;
  for (let i = 0; i < majorSegments; i++) {
    for (let j = 0; j < minorSegments; j++) {
      const u0 = i / majorSegments;
      const u1 = (i + 1) / majorSegments;
      const v0 = j / minorSegments;
      const v1 = (j + 1) / minorSegments;
      uvData[ui++] = u0;
      uvData[ui++] = v0;
      uvData[ui++] = u1;
      uvData[ui++] = v0;
      uvData[ui++] = u1;
      uvData[ui++] = v1;
      uvData[ui++] = u0;
      uvData[ui++] = v1;
    }
  }

  const vertices = new Float32Array(verts);

  return {
    vertices,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: recomputeLoopNormals(vertices, topology),
    topology,
    uvs: uvData,
  };
}

// ---------------------------------------------------------------------------
// Cylinder — shared-vertex with seam wrap, single center vertices for caps
// ---------------------------------------------------------------------------

export interface CylinderParams {
  /** Radius in meters (default 0.5). */
  radius?: number;
  /** Total height in meters (default 1). */
  height?: number;
  /** Vertex count around the circumference (default 32). */
  segments?: number;
}

export function generateCylinder(
  params: CylinderParams = {},
): PrimitiveGeometry {
  const radius = params.radius ?? 0.5;
  const segments = params.segments ?? 32;
  const halfH = (params.height ?? 1) * 0.5;

  // Shared vertices:
  //   - 2 rings of `segments` vertices (bottom ring, top ring) for sides
  //   - 1 bottom center + 1 top center for caps
  //   - Bottom ring reused for bottom cap, top ring reused for top cap
  // Total: segments (bottom ring) + segments (top ring) + 2 (centers)
  const vertCount = segments * 2 + 2;
  const verts = new Float32Array(vertCount * 3);

  // Bottom ring: indices 0..segments-1
  for (let seg = 0; seg < segments; seg++) {
    const theta = (2 * Math.PI * seg) / segments;
    const idx = seg;
    verts[idx * 3] = Math.cos(theta) * radius;
    verts[idx * 3 + 1] = Math.sin(theta) * radius;
    verts[idx * 3 + 2] = -halfH;
  }

  // Top ring: indices segments..2*segments-1
  for (let seg = 0; seg < segments; seg++) {
    const theta = (2 * Math.PI * seg) / segments;
    const idx = segments + seg;
    verts[idx * 3] = Math.cos(theta) * radius;
    verts[idx * 3 + 1] = Math.sin(theta) * radius;
    verts[idx * 3 + 2] = halfH;
  }

  // Bottom center: index 2*segments
  const botCenter = segments * 2;
  verts[botCenter * 3] = 0;
  verts[botCenter * 3 + 1] = 0;
  verts[botCenter * 3 + 2] = -halfH;

  // Top center: index 2*segments+1
  const topCenter = segments * 2 + 1;
  verts[topCenter * 3] = 0;
  verts[topCenter * 3 + 1] = 0;
  verts[topCenter * 3 + 2] = halfH;

  // Topology
  // Side quads: segments faces × 4 corners
  // Bottom cap tris: segments faces × 3 corners
  // Top cap tris: segments faces × 3 corners
  const sideFaces = segments;
  const capFaces = segments * 2;
  const totalFaces = sideFaces + capFaces;
  const totalCorners = sideFaces * 4 + capFaces * 3;

  const offsets = new Uint32Array(totalFaces + 1);
  const corners = new Uint32Array(totalCorners);

  let ci = 0;
  let fi = 0;

  const botIdx = (seg: number) => seg % segments;
  const topIdx = (seg: number) => segments + (seg % segments);

  // Side quads
  for (let seg = 0; seg < segments; seg++) {
    offsets[fi] = ci;
    fi++;
    corners[ci++] = botIdx(seg);
    corners[ci++] = botIdx(seg + 1);
    corners[ci++] = topIdx(seg + 1);
    corners[ci++] = topIdx(seg);
  }

  // Bottom cap triangles (CW from below → CCW viewed from -Z)
  for (let seg = 0; seg < segments; seg++) {
    offsets[fi] = ci;
    fi++;
    corners[ci++] = botCenter;
    corners[ci++] = botIdx(seg + 1);
    corners[ci++] = botIdx(seg);
  }

  // Top cap triangles
  for (let seg = 0; seg < segments; seg++) {
    offsets[fi] = ci;
    fi++;
    corners[ci++] = topCenter;
    corners[ci++] = topIdx(seg);
    corners[ci++] = topIdx(seg + 1);
  }

  offsets[totalFaces] = ci;

  const topology: PolygonTopology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };
  const vertices = new Float32Array(verts);

  // Per-corner UVs
  const uvData = new Float32Array(totalCorners * 2);
  let ui = 0;

  // Side UVs
  for (let seg = 0; seg < segments; seg++) {
    const u0 = seg / segments;
    const u1 = (seg + 1) / segments;
    uvData[ui++] = u0;
    uvData[ui++] = 0;
    uvData[ui++] = u1;
    uvData[ui++] = 0;
    uvData[ui++] = u1;
    uvData[ui++] = 1;
    uvData[ui++] = u0;
    uvData[ui++] = 1;
  }

  // Bottom cap UVs (circular projection)
  for (let seg = 0; seg < segments; seg++) {
    const theta0 = (2 * Math.PI * seg) / segments;
    const theta1 = (2 * Math.PI * (seg + 1)) / segments;
    uvData[ui++] = 0.5;
    uvData[ui++] = 0.5; // center
    uvData[ui++] = 0.5 + 0.5 * Math.cos(theta1);
    uvData[ui++] = 0.5 + 0.5 * Math.sin(theta1);
    uvData[ui++] = 0.5 + 0.5 * Math.cos(theta0);
    uvData[ui++] = 0.5 + 0.5 * Math.sin(theta0);
  }

  // Top cap UVs (circular projection)
  for (let seg = 0; seg < segments; seg++) {
    const theta0 = (2 * Math.PI * seg) / segments;
    const theta1 = (2 * Math.PI * (seg + 1)) / segments;
    uvData[ui++] = 0.5;
    uvData[ui++] = 0.5; // center
    uvData[ui++] = 0.5 + 0.5 * Math.cos(theta0);
    uvData[ui++] = 0.5 + 0.5 * Math.sin(theta0);
    uvData[ui++] = 0.5 + 0.5 * Math.cos(theta1);
    uvData[ui++] = 0.5 + 0.5 * Math.sin(theta1);
  }

  return {
    vertices,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: recomputeLoopNormals(vertices, topology),
    topology,
    uvs: uvData,
  };
}

// ---------------------------------------------------------------------------
// Cone — shared base ring + single apex and base center
// ---------------------------------------------------------------------------

export interface ConeParams {
  /** Base radius (default 0.5). */
  radius?: number;
  /** Total height (default 1). */
  height?: number;
  /** Vertex count around the base (default 32). */
  segments?: number;
}

export function generateCone(params: ConeParams = {}): PrimitiveGeometry {
  const radius = params.radius ?? 0.5;
  const segments = params.segments ?? 32;
  const halfH = (params.height ?? 1) * 0.5;

  // Shared vertices:
  //   - base ring: segments vertices
  //   - apex: 1 vertex
  //   - base center: 1 vertex
  const vertCount = segments + 2;
  const verts = new Float32Array(vertCount * 3);

  for (let seg = 0; seg < segments; seg++) {
    const theta = (2 * Math.PI * seg) / segments;
    verts[seg * 3] = Math.cos(theta) * radius;
    verts[seg * 3 + 1] = Math.sin(theta) * radius;
    verts[seg * 3 + 2] = -halfH;
  }

  const apex = segments;
  verts[apex * 3] = 0;
  verts[apex * 3 + 1] = 0;
  verts[apex * 3 + 2] = halfH;

  const baseCenter = segments + 1;
  verts[baseCenter * 3] = 0;
  verts[baseCenter * 3 + 1] = 0;
  verts[baseCenter * 3 + 2] = -halfH;

  const totalFaces = segments * 2;
  const totalCorners = totalFaces * 3;
  const offsets = new Uint32Array(totalFaces + 1);
  const corners = new Uint32Array(totalCorners);

  let ci = 0;
  let fi = 0;
  const ringIdx = (seg: number) => seg % segments;

  // Side triangles
  for (let seg = 0; seg < segments; seg++) {
    offsets[fi++] = ci;
    corners[ci++] = apex;
    corners[ci++] = ringIdx(seg);
    corners[ci++] = ringIdx(seg + 1);
  }

  // Base cap triangles
  for (let seg = 0; seg < segments; seg++) {
    offsets[fi++] = ci;
    corners[ci++] = baseCenter;
    corners[ci++] = ringIdx(seg + 1);
    corners[ci++] = ringIdx(seg);
  }

  offsets[totalFaces] = ci;

  const topology: PolygonTopology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };
  const vertices = new Float32Array(verts);
  const uvData = new Float32Array(totalCorners * 2);
  let ui = 0;

  // Side UVs: apex at top center of each wedge, base along [0,1]
  for (let seg = 0; seg < segments; seg++) {
    const u0 = seg / segments;
    const u1 = (seg + 1) / segments;
    uvData[ui++] = (u0 + u1) * 0.5;
    uvData[ui++] = 1;
    uvData[ui++] = u0;
    uvData[ui++] = 0;
    uvData[ui++] = u1;
    uvData[ui++] = 0;
  }

  // Base UVs: circular projection
  for (let seg = 0; seg < segments; seg++) {
    const theta0 = (2 * Math.PI * seg) / segments;
    const theta1 = (2 * Math.PI * (seg + 1)) / segments;
    uvData[ui++] = 0.5;
    uvData[ui++] = 0.5;
    uvData[ui++] = 0.5 + 0.5 * Math.cos(theta1);
    uvData[ui++] = 0.5 + 0.5 * Math.sin(theta1);
    uvData[ui++] = 0.5 + 0.5 * Math.cos(theta0);
    uvData[ui++] = 0.5 + 0.5 * Math.sin(theta0);
  }

  return {
    vertices,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: recomputeLoopNormals(vertices, topology),
    topology,
    uvs: uvData,
  };
}

// ---------------------------------------------------------------------------
// Icosphere — subdivided icosahedron
// ---------------------------------------------------------------------------

export interface IcosphereParams {
  /** Radius in meters (default 0.5). */
  radius?: number;
  /** Subdivision levels (default 2). */
  subdivisions?: number;
}

export function generateIcosphere(
  params: IcosphereParams = {},
): PrimitiveGeometry {
  const radius = params.radius ?? 0.5;
  const subdivisions = params.subdivisions ?? 2;
  const t = (1 + Math.sqrt(5)) / 2;

  const positions: [number, number, number][] = [
    [-1, t, 0],
    [1, t, 0],
    [-1, -t, 0],
    [1, -t, 0],
    [0, -1, t],
    [0, 1, t],
    [0, -1, -t],
    [0, 1, -t],
    [t, 0, -1],
    [t, 0, 1],
    [-t, 0, -1],
    [-t, 0, 1],
  ];

  const faces: [number, number, number][] = [
    [0, 11, 5],
    [0, 5, 1],
    [0, 1, 7],
    [0, 7, 10],
    [0, 10, 11],
    [1, 5, 9],
    [5, 11, 4],
    [11, 10, 2],
    [10, 7, 6],
    [7, 1, 8],
    [3, 9, 4],
    [3, 4, 2],
    [3, 2, 6],
    [3, 6, 8],
    [3, 8, 9],
    [4, 9, 5],
    [2, 4, 11],
    [6, 2, 10],
    [8, 6, 7],
    [9, 8, 1],
  ];

  const normalize = ([x, y, z]: [number, number, number]): [
    number,
    number,
    number,
  ] => {
    const len = Math.hypot(x, y, z) || 1;
    return [(x / len) * radius, (y / len) * radius, (z / len) * radius];
  };

  for (let i = 0; i < positions.length; i++)
    positions[i] = normalize(positions[i]);

  const midpointCache = new Map<number, number>();
  const midpointIndex = (a: number, b: number): number => {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const key = lo * 65536 + hi;
    const cached = midpointCache.get(key);
    if (cached !== undefined) return cached;

    const pa = positions[a];
    const pb = positions[b];
    const mid = normalize([
      (pa[0] + pb[0]) * 0.5,
      (pa[1] + pb[1]) * 0.5,
      (pa[2] + pb[2]) * 0.5,
    ]);
    const idx = positions.length;
    positions.push(mid);
    midpointCache.set(key, idx);
    return idx;
  };

  let tris = faces;
  for (let i = 0; i < subdivisions; i++) {
    midpointCache.clear();
    const next: [number, number, number][] = [];
    for (const [a, b, c] of tris) {
      const ab = midpointIndex(a, b);
      const bc = midpointIndex(b, c);
      const ca = midpointIndex(c, a);
      next.push([a, ab, ca], [b, bc, ab], [c, ca, bc], [ab, bc, ca]);
    }
    tris = next;
  }

  const vertices = new Float32Array(positions.length * 3);
  for (let i = 0; i < positions.length; i++) {
    vertices[i * 3] = positions[i][0];
    vertices[i * 3 + 1] = positions[i][1];
    vertices[i * 3 + 2] = positions[i][2];
  }

  const triangleIndices = new Uint32Array(tris.length * 3);
  for (let i = 0; i < tris.length; i++) {
    triangleIndices[i * 3] = tris[i][0];
    triangleIndices[i * 3 + 1] = tris[i][1];
    triangleIndices[i * 3 + 2] = tris[i][2];
  }

  const topology = topologyFromTriangles(
    asSharedTriangleIndices(triangleIndices),
  );
  const cornerCount = topology.corners.length;
  const uvs = new Float32Array(cornerCount * 2);
  for (let i = 0; i < cornerCount; i++) {
    const v = topology.corners[i];
    const x = vertices[v * 3];
    const y = vertices[v * 3 + 1];
    const z = vertices[v * 3 + 2];
    const nx = x / radius;
    const ny = y / radius;
    const nz = z / radius;
    uvs[i * 2] = 0.5 + Math.atan2(ny, nx) / (2 * Math.PI);
    uvs[i * 2 + 1] = 0.5 - Math.asin(Math.max(-1, Math.min(1, nz))) / Math.PI;
  }

  return {
    vertices,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: recomputeLoopNormals(vertices, topology),
    topology,
    uvs,
  };
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Generate the starter geometry for a Skin Armature — two vertices connected
 * by a single wire edge. The edge carries EDGE_WIRE so the Skin modifier can
 * identify it. There are no face polygons; indices is empty.
 */
function generateSkinArmature(): PrimitiveGeometry {
  // Two vertices along the Y axis, 1 unit total length
  const vertices = new Float32Array([-0, 0, -0.5, 0, 0, 0.5]);

  // One wire edge: v0 → v1
  const edges = new Uint32Array([0, 1]);
  const edgeFlags = new Uint8Array([EDGE_WIRE]);

  const topology: import("./types.ts").PolygonTopology = {
    offsets: new Uint32Array([0]), // 0 faces
    corners: new Uint32Array(0),
    edges,
    cornerEdges: new Uint32Array(0),
    edgeFlags,
  };

  return {
    vertices,
    indices: EMPTY_RENDER_TRIANGLE_INDICES,
    loopNormals: asLoopNormals(new Float32Array(0)),
    topology,
    uvs: new Float32Array(0),
  };
}

/** Generate geometry for a given primitive kind. */
export function generatePrimitive(kind: PrimitiveKind): PrimitiveGeometry {
  switch (kind) {
    case "cube":
      return generateCube();
    case "circle":
      return generateCircle();
    case "sphere":
      return generateSphere();
    case "icosphere":
      return generateIcosphere();
    case "plane":
      return generatePlane();
    case "torus":
      return generateTorus();
    case "curve":
      return generateCurveGeometry(createDefaultCurveData());
    case "cylinder":
      return generateCylinder();
    case "cone":
      return generateCone();
    case "skin_armature":
      return generateSkinArmature();
    case "empty":
      throw new Error("Empty objects have no geometry");
  }
}

/** Generate default CurveData for a new curve object. */
export { createDefaultCurveData } from "./curvemath.ts";
